import { useEffect, useState } from "react";
import SouthavenHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const navItems = [
  { label: "Processing", href: "#processing" },
  { label: "Services", href: "#services" },
  { label: "Local Area", href: "#local-area" },
  { label: "Timing", href: "#timing" },
  { label: "Destinations", href: "#destinations" },
  { label: "Facilities", href: "#facilities" },
  { label: "Compare", href: "#compare" },
  { label: "FAQ", href: "#faq" },
  { label: "Resources", href: "#resources" },
];

const faqs = [
  { q: "How fast can I get a passport in Southaven, MS?", a: "Our emergency service delivers passports in as little as 24 hours. Expedited takes 3–5 business days. With FedEx's World Hub in Memphis just minutes away, we're especially equipped to handle urgent logistics-industry travel needs." },
  { q: "Where is the nearest passport acceptance facility to Southaven?", a: "The Southaven Post Office on Goodman Road is the most convenient. Memphis facilities across the Tennessee state line are also accessible. We direct you to whichever has the shortest current wait time." },
  { q: "Can FedEx employees in Southaven get expedited passports?", a: "Yes. FedEx World Hub employees and supply chain professionals with urgent international business travel are a core part of our client base in the Memphis metro area." },
  { q: "Is there a Regional Passport Agency near Southaven?", a: "The Houston Regional Passport Agency is the nearest, about 8 hours south. For emergency processing, our courier service is far faster and more convenient — we handle everything remotely." },
  { q: "Do I need an appointment for a passport in Southaven?", a: "Most DeSoto County facilities are walk-in. Memphis facilities may offer appointments. We advise on the best option based on your timeline." },
  { q: "What documents do I need for a passport in Southaven?", a: "Certified birth certificate with raised seal, valid government photo ID, compliant passport photo, and DS-11 or DS-82 form. We verify all documents before your facility visit." },
  { q: "Can I use Memphis passport acceptance facilities from Southaven?", a: "Yes, many Southaven residents use Memphis facilities which are often 15–20 minutes away. We guide you to the best option based on current wait times in both markets." },
  { q: "How much does passport expediting cost from Southaven?", a: "Government fees are $130–$190. Our service fees start at $99 for standard expedited. Emergency 24-hour service is available at higher tiers. Best price guarantee." },
];

const destinations = [
  { name: "Cancun", country: "Mexico", flag: "🇲🇽", desc: "Most popular spring break and family destination. Flights from Memphis International Airport." },
  { name: "Europe", country: "Various", flag: "🇪🇺", desc: "FedEx corporate travel to Amsterdam, London, and Paris hubs for logistics professionals." },
  { name: "China", country: "China", flag: "🇨🇳", desc: "Supply chain and manufacturing visits for DeSoto County corporate professionals." },
  { name: "Caribbean Cruises", country: "Various", flag: "🚢", desc: "New Orleans cruise port is popular with Southaven families — just 3.5 hours south." },
  { name: "Japan", country: "Japan", flag: "🇯🇵", desc: "FedEx Asian operations and business travel from the Memphis logistics hub." },
  { name: "Bahamas", country: "Bahamas", flag: "🇧🇸", desc: "Family cruise destination. Popular long-weekend escape for DeSoto County families." },
];

const facilities = [
  { name: "Southaven Post Office", address: "7180 Airways Blvd, Southaven, MS 38671", phone: "(662) 349-5300", hours: "Mon–Fri 9:00 AM – 4:00 PM", wait: "10–25 min" },
  { name: "Horn Lake Post Office", address: "3700 Goodman Rd W, Horn Lake, MS 38637", phone: "(662) 393-6470", hours: "Mon–Fri 9:00 AM – 4:00 PM", wait: "10–20 min" },
  { name: "Olive Branch Post Office", address: "8475 Macon Rd, Olive Branch, MS 38654", phone: "(662) 895-5155", hours: "Mon–Fri 9:00 AM – 4:00 PM", wait: "10–20 min" },
];

const testimonials = [
  { name: "James R.", city: "Southaven", stars: 5, text: "FedEx sent me to Amsterdam with 10 days notice. Fast Passport Center had my passport renewed in 3 days. Incredible speed for DeSoto County." },
  { name: "Amanda T.", city: "Goodman Road, Southaven", stars: 5, text: "Our family's passports expired before our Caribbean cruise. All 4 done in 4 days. These guys are the real deal." },
  { name: "Mark C.", city: "Horn Lake", stars: 5, text: "Supply chain manager with an urgent China trip. Used the emergency service — passport in 36 hours. Will never use anyone else." },
];

export default function SouthavenPage() {
  useEffect(() => {
    document.title = "Emergency Passport Southaven, MS — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    setLink("canonical", "https://fastpassportcenter.com/southaven/");
    setMeta("description", "Emergency and expedited passport services in Southaven, MS — as fast as 24 hours. Serving FedEx employees, DeSoto County families, and Memphis metro travelers. A+ BBB rated.");
    setMeta("geo.placename", "Southaven, Mississippi");
    setMeta("geo.region", "US-MS");
    setMeta("geo.position", "34.9887;-90.0126");
    setMeta("ICBM", "34.9887, -90.0126");
    return () => { document.querySelector('link[rel="canonical"]')?.remove(); };
  }, []);

  return (
    <div className="font-sans">
      <SouthavenHeroSection />
      <StickyNav />
      <ProcessingSection />
      <ServicesSection />
      <LocalAreaSection />
      <TravelTimingSection />
      <DestinationsSection />
      <TestimonialsSection />
      <AcceptanceFacilitiesSection />
      <ComparisonSection />
      <MistakesSection />
      <HowItWorksSection />
      <FAQSection />
      <HelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/southaven" state="MS" />
      <FinalCTA />
    </div>
  );
}

function StickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = navItems.map((n) => document.querySelector(n.href));
      const scroll = window.scrollY + 120;
      for (let i = sections.length - 1; i >= 0; i--) {
        const s = sections[i];
        if (s && s.getBoundingClientRect().top + window.scrollY <= scroll) { setActive(navItems[i].href); return; }
      }
      setActive("");
    };
    window.addEventListener("scroll", handle);
    return () => window.removeEventListener("scroll", handle);
  }, []);
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center gap-1 overflow-x-auto py-2">
          {navItems.map((item) => (<a key={item.href} href={item.href} className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${active === item.href ? "bg-[#1A365D] text-white" : "text-gray-600 hover:bg-gray-100"}`}>{item.label}</a>))}
        </div>
      </div>
    </div>
  );
}

function ProcessingSection() {
  return (
    <section id="processing" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How Fast Can You Get a Passport in Southaven?</h2>
          <p className="text-gray-500 max-w-2xl mx-auto">Corporate traveler, family, or first-timer — three tiers for every timeline.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $299", badge: "Fastest", desc: "For FedEx and logistics professionals with immediate international business travel.", features: ["24-hour turnaround available", "Hand-carry courier service", "Real-time tracking", "Phone support 7 days/week"] },
            { title: "Expedited", time: "3–5 Business Days", price: "From $149", badge: "Most Popular", desc: "The most popular choice for DeSoto County and Memphis metro families.", features: ["3–5 day guaranteed delivery", "Application review included", "Photo compliance check", "Email + phone support"] },
            { title: "Standard", time: "7–10 Business Days", price: "From $99", badge: "Best Value", desc: "Far faster than government standard. Ideal for planned travel.", features: ["7–10 day processing", "Complete application guidance", "Document checklist", "Email support"] },
          ].map((plan, i) => (
            <div key={i} className={`bg-white border rounded-xl p-7 flex flex-col ${plan.badge === "Most Popular" ? "border-[#F6C344] ring-1 ring-[#F6C344]" : "border-gray-200"}`}>
              <div className="flex items-center justify-between mb-4">
                <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${plan.badge === "Fastest" ? "bg-red-100 text-red-700" : plan.badge === "Most Popular" ? "bg-[#F6C344]/20 text-[#1A365D]" : "bg-gray-100 text-gray-700"}`}>{plan.badge}</span>
                <span className="text-[#1A365D] font-bold text-lg">{plan.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-xl mb-1">{plan.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{plan.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed mb-5 flex-1">{plan.desc}</p>
              <ul className="space-y-2 mb-6">{plan.features.map((f, j) => <li key={j} className="flex items-center gap-2 text-sm text-gray-600"><i className="ri-check-line text-[#F6C344] flex-shrink-0"></i>{f}</li>)}</ul>
              <a href="https://fastpassportcenter.com/checkout/" className="block text-center bg-[#1A365D] hover:bg-[#0f2341] text-white font-semibold text-sm py-3 rounded-lg transition-all whitespace-nowrap">Get Started</a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "New Passport", desc: "First-time applications for DeSoto County residents." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for corporate travelers and families." },
    { icon: "ri-alert-line", title: "Lost/Stolen", desc: "Emergency replacement with DS-64 form guidance." },
    { icon: "ri-user-smile-line", title: "Child Passport", desc: "Complete guidance for minors under 16." },
    { icon: "ri-time-line", title: "Emergency", desc: "24-hour processing for urgent corporate travel." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Post-marriage or legal name updates." },
  ];
  return (
    <section id="services" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for Southaven &amp; DeSoto County</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4"><i className={`${s.icon} text-[#1A365D] text-xl`}></i></div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocalAreaSection() {
  return (
    <section id="local-area" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services in Southaven, Mississippi</h2>
        </div>
        <div className="max-w-3xl mx-auto text-gray-600 text-base leading-relaxed space-y-5">
          <p>Southaven is Mississippi's third-largest city and the anchor of DeSoto County — the fastest-growing county in Mississippi. Situated just south of Memphis, Tennessee, Southaven functions as a suburb of one of America's most significant logistics hubs. With FedEx World Hub, AutoZone, Thomas &amp; Betts, and dozens of major distribution and manufacturing facilities within minutes, the Southaven metro area generates substantial and urgent corporate passport demand.</p>
          <p>FedEx alone employs tens of thousands of workers in the greater Memphis-DeSoto County area, many of whom travel internationally for corporate operations, hub oversight, and business development. Supply chain managers, operations directors, and logistics executives regularly receive short-notice international travel assignments to Europe, Asia, and Latin America. The urgency of these trips — combined with the often bureaucratic and slow nature of government passport processing — makes our expedited courier service an essential resource for DeSoto County professionals.</p>
          <p>Beyond the corporate sector, Southaven's growing residential communities in Goodman Road, Snowden Grove, and the Southaven Town Center areas house thousands of families who increasingly travel internationally for spring break, summer vacations, and holiday cruises. The New Orleans cruise port — about 3.5 hours south — is a popular embarkation point for Caribbean cruises. Memphis International Airport, just 15 minutes north, provides international connections through Chicago, Dallas, and Atlanta.</p>
          <p>DeSoto County's school districts are among the largest in Mississippi, and student groups from Southaven High School and DeSoto Central regularly participate in international academic competitions, music festivals, and cultural exchange programs in Europe and Latin America. Teacher and chaperone passports often need renewal on a tight back-to-school timeline. Our service ensures no student trip is delayed due to an adult passport expiration.</p>
        </div>
      </div>
    </section>
  );
}

function TravelTimingSection() {
  return (
    <section id="timing" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Travel Timing</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">When Southaven Travelers Need Passports</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { month: "Year-Round", title: "Corporate Travel", desc: "FedEx and DeSoto County logistics professionals travel internationally on unpredictable corporate timelines." },
            { month: "March–April", title: "Spring Break", desc: "DeSoto County families head to Cancun and Caribbean. New Orleans cruise season peaks." },
            { month: "May–June", title: "Summer Travel", desc: "European and island vacations for Southaven families. School group trips begin." },
            { month: "July–August", title: "Back to School Prep", desc: "Teacher and student passports for fall international programs and competitions." },
            { month: "September–October", title: "Renewal Season", desc: "Ideal time to renew before the holiday travel and cruise rush." },
            { month: "November–December", title: "Holiday Rush", desc: "Winter cruises from New Orleans and international family reunions." },
          ].map((t, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-wider mb-2">{t.month}</p>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{t.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DestinationsSection() {
  return (
    <section id="destinations" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Southaven Travelers Go</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {destinations.map((d, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{d.flag}</span>
                <div><h3 className="font-bold text-[#1A365D] text-lg">{d.name}</h3><p className="text-gray-400 text-xs">{d.country}</p></div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{d.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section id="testimonials" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Southaven Travelers Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-1 mb-3">{[...Array(t.stars)].map((_, j) => <i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>)}</div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#EEF2F8] flex items-center justify-center"><i className="ri-user-line text-[#1A365D]"></i></div>
                <div><p className="font-semibold text-[#1A365D] text-sm">{t.name}</p><p className="text-gray-400 text-xs">{t.city}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AcceptanceFacilitiesSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="facilities" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Acceptance Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Acceptance Facilities Near Southaven</h2>
        </div>
        <div className="space-y-4 max-w-3xl mx-auto">
          {facilities.map((f, i) => (
            <div key={i} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8]"><i className="ri-map-pin-line text-[#1A365D]"></i></div>
                  <div><h3 className="font-bold text-[#1A365D] text-base">{f.name}</h3><p className="text-gray-400 text-xs">{f.address}</p></div>
                </div>
                <i className={`${open === i ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-xl`}></i>
              </button>
              {open === i && (
                <div className="px-5 pb-5 pt-0 border-t border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Phone</p><p className="text-[#1A365D] text-sm font-medium">{f.phone}</p></div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Hours</p><p className="text-[#1A365D] text-sm font-medium">{f.hours}</p></div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Avg Wait</p><p className="text-[#1A365D] text-sm font-medium">{f.wait}</p></div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonSection() {
  const rows = [
    { feature: "Processing Speed", us: "As fast as 24 hours", them: "10–13 weeks standard" },
    { feature: "Office Visit Required", us: "No — mail or drop-off", them: "Yes — in-person required" },
    { feature: "Application Review", us: "Expert review before submission", them: "No review — errors cause delays" },
    { feature: "Tracking", us: "Real-time status updates", them: "Limited status info" },
    { feature: "Customer Support", us: "Phone + email, 7 days/week", them: "Phone only, limited hours" },
    { feature: "Price Guarantee", us: "Best price guarantee", them: "No price matching" },
    { feature: "Corporate Travel Support", us: "Priority handling for business travel", them: "No corporate priority" },
    { feature: "Refund Policy", us: "Satisfaction guarantee", them: "No refunds after submission" },
  ];
  return (
    <section id="compare" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. DIY Government Processing</h2>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-3 border-b border-gray-200 bg-[#EEF2F8]">
            <div className="p-4 font-bold text-[#1A365D] text-sm">Feature</div>
            <div className="p-4 font-bold text-[#1A365D] text-sm text-center">Fast Passport Center</div>
            <div className="p-4 font-bold text-gray-500 text-sm text-center">Government Standard</div>
          </div>
          {rows.map((row, i) => (
            <div key={i} className={`grid grid-cols-3 ${i !== rows.length - 1 ? "border-b border-gray-100" : ""}`}>
              <div className="p-4 text-sm text-gray-600 font-medium">{row.feature}</div>
              <div className="p-4 text-sm text-[#1A365D] font-semibold text-center bg-[#F7F8FA]/50">{row.us}</div>
              <div className="p-4 text-sm text-gray-500 text-center">{row.them}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MistakesSection() {
  const mistakes = [
    { title: "Corporate Booking Before Renewal", desc: "FedEx and logistics professionals often book international flights before checking passport expiration dates. Always verify first." },
    { title: "Wrong Photo", desc: "Photos from Southaven convenience stores are frequently rejected. We verify photo compliance before submission." },
    { title: "Missing Raised Seal", desc: "Birth certificates without an embossed raised seal are rejected. We check all documents before your facility visit." },
    { title: "Late Application Start", desc: "Many DeSoto County residents start just 3 weeks before travel. Start 6–8 weeks early for standard processing." },
    { title: "Form Confusion", desc: "DS-11 vs DS-82 errors cause major delays. We identify the correct form for your situation immediately." },
  ];
  return (
    <section id="mistakes" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Common Mistakes</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">5 Mistakes Southaven Applicants Make</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 mb-4"><i className="ri-close-circle-line text-red-500 text-xl"></i></div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{m.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    { num: "01", title: "Choose Your Service", desc: "Emergency, expedited, or standard — matched to your departure date." },
    { num: "02", title: "Complete Application", desc: "We guide you through DS-11 or DS-82 step by step." },
    { num: "03", title: "Visit Acceptance Facility", desc: "We direct you to the Southaven or DeSoto County facility with shortest wait." },
    { num: "04", title: "We Handle Processing", desc: "Hand-carried to the State Department for priority processing." },
    { num: "05", title: "Receive Your Passport", desc: "Secure delivery with real-time tracking." },
  ];
  return (
    <section id="how-it-works" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">How It Works</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">5 Steps to Your Passport</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-lg mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="py-16 md:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Southaven, MS Passport Questions</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                <span className="font-semibold text-[#1A365D] text-sm pr-4">{faq.q}</span>
                <i className={`${open === i ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-xl flex-shrink-0`}></i>
              </button>
              {open === i && <div className="px-5 pb-5 pt-0 border-t border-gray-100"><p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p></div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HelpfulResourcesSection() {
  return (
    <section id="resources" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Southaven Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: "Travel.State.Gov", desc: "Official U.S. Department of State passport information.", link: "https://travel.state.gov/content/travel/en/passports.html" },
            { title: "Memphis International Airport", desc: "MEM airport — nearest major international connection hub.", link: "https://www.flymemphis.com/" },
            { title: "CDC Travel Health", desc: "Health recommendations and vaccination info for international travel.", link: "https://wwwnc.cdc.gov/travel" },
          ].map((r, i) => (
            <a key={i} href={r.link} target="_blank" rel="noopener noreferrer" className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all group">
              <h3 className="font-bold text-[#1A365D] text-lg mb-2 group-hover:text-[#F6C344] transition-colors">{r.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{r.desc}</p>
              <span className="inline-flex items-center gap-1 text-[#1A365D] text-sm font-semibold group-hover:text-[#F6C344] transition-colors">Visit <i className="ri-arrow-right-line"></i></span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-20 md:py-28 bg-[#1A365D]">
      <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-5">Ready to Get Your Passport in Southaven?</h2>
        <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">Corporate traveler, DeSoto County family, or cruise-goer — we'll get your passport ready when you need it.</p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">Start My Passport <i className="ri-arrow-right-line"></i></a>
        <p className="text-white/50 text-sm mt-6">Call us at <a href="tel:8772530084" className="text-[#F6C344] hover:underline">877-253-0084</a> for immediate assistance</p>
      </div>
    </section>
  );
}